import { Centrifuge } from 'centrifuge';
import { api } from '@marine/api-client/api';
import { tenantAuthStore } from '@/modules/tenant/state/tenantAuth';
import { resetChannelRefCounts } from './useChannel';
import type { ConnectionState, RealtimeTokenResponse } from './types';

/**
 * Singleton Centrifuge client.
 *
 * Why a singleton: Centrifugo's server-side connection accounting is
 * per-client. Opening one WSS per hook would balloon connection counts
 * and fragment presence. Every useChannel call shares the same socket.
 *
 * Token flow:
 *   1. Browser is signed into Laravel via Sanctum session cookie.
 *   2. On connect and on refresh, centrifuge-js calls getToken() which
 *      hits GET /api/realtime/token — Laravel decides which
 *      channels this user may subscribe to and returns a short-TTL JWT.
 *   3. Centrifugo verifies the JWT with its HS256 secret. No shared
 *      secret is ever sent to the browser.
 *
 * The URL is derived from window.location so the same code works on
 * every tenant subdomain (e.g. wss://stws.dms44.com/connection/websocket).
 * Traefik routes /connection/* to the centrifugo container.
 */

let singleton: Centrifuge | null = null;
let connectionState: ConnectionState = 'disconnected';
const stateListeners = new Set<(s: ConnectionState) => void>();

function setState(next: ConnectionState) {
  if (connectionState === next) return;
  connectionState = next;
  stateListeners.forEach((l) => l(next));
}

async function fetchToken(): Promise<string> {
  try {
    const { data } = await api.get<RealtimeTokenResponse>(
      '/api/realtime/token',
    );
    return data.token;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 401 || status === 403) {
      // Session is gone — force a hard logout so the user sees the login
      // screen instead of an endless reconnect loop. Marking `temporary:
      // false` tells centrifuge-js not to retry this token fetch.
      disposeCentrifuge();
      tenantAuthStore.getState().logout();
      const e = new Error('session_revoked') as Error & { temporary?: boolean };
      e.temporary = false;
      throw e;
    }
    throw err;
  }
}

function buildUrl(): string {
  if (typeof window === 'undefined') {
    // SSR fallback — Centrifugo runs on 8787, not the Laravel port.
    return 'ws://localhost:8787/connection/websocket';
  }
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}/connection/websocket`;
}

/**
 * Lazy-initialise the Centrifuge singleton. Safe to call many times; the
 * underlying socket is opened once and reused. Disconnecting from here
 * would race with other hooks, so callers must never call .disconnect()
 * directly — use disposeCentrifuge() at sign-out time.
 */
export function getCentrifuge(): Centrifuge {
  if (singleton) return singleton;

  // Short-circuit callers that try to open realtime before the user is
  // signed in — the token endpoint would 401 anyway, and propagating the
  // misuse as an explicit error catches the bug at the call site rather
  // than buried inside centrifuge-js retry logic.
  if (!tenantAuthStore.getState().isAuthenticated) {
    throw new Error('Cannot open realtime connection before auth.');
  }

  const client = new Centrifuge(buildUrl(), {
    getToken: async () => {
      try {
        return await fetchToken();
      } catch (err) {
        // Surface the failure to the client so it enters the `error` state
        // and the user-facing "reconnecting..." UI can appear.
        setState('error');
        throw err;
      }
    },
  });

  client.on('connecting', () => setState('connecting'));
  client.on('connected', () => setState('connected'));
  client.on('disconnected', () => setState('disconnected'));
  client.on('error', () => setState('error'));

  client.connect();
  singleton = client;
  return client;
}

/**
 * Subscribe to the current connection state. Returns an unsubscribe fn.
 * Used by a small status indicator in the app shell.
 */
export function onConnectionState(
  listener: (s: ConnectionState) => void,
): () => void {
  stateListeners.add(listener);
  listener(connectionState);
  return () => {
    stateListeners.delete(listener);
  };
}

export function getConnectionState(): ConnectionState {
  return connectionState;
}

/**
 * Tear down the singleton — call on sign-out so the next user doesn't
 * inherit the previous session's socket and token. Also resets the
 * per-channel refcount map in useChannel so the next login doesn't see
 * "already subscribed" ghosts from the previous session.
 */
export function disposeCentrifuge(): void {
  if (!singleton) return;
  singleton.disconnect();
  singleton.removeAllListeners();
  singleton = null;
  setState('disconnected');
  resetChannelRefCounts();
}

/**
 * Test hook — resets the module state between vitest cases.
 */
export function __resetCentrifugeForTests(): void {
  if (singleton) {
    try {
      singleton.removeAllListeners();
      singleton.disconnect();
    } catch {
      // test doubles may not implement these
    }
  }
  singleton = null;
  connectionState = 'disconnected';
  stateListeners.clear();
}

// HMR guard — dev-only. When Vite hot-swaps this module the existing WS
// stays open on the old module instance and leaks; we tear it down so
// the reloaded module gets a clean singleton.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    disposeCentrifuge();
  });
}
